import uuid from 'uuid-random';
import {
    find, pipe, toPairs, prop, any, equals, over, lensProp, assocPath,
    append, prepend, isEmpty, pick, values, flatten, keys, assoc,
    intersection, flip, identity, difference, reduce, last, omit
} from 'ramda';

import { getLayerId, layersMeta as layersMetaDefault } from './layers';
import { POPUP_TEMPLATE_IDS, SINGLETON_MODE } from './constants';

export const getPopupIdByTemplateId = (templateId) => pipe(
    toPairs,
    find(any(equals(templateId))),
    prop('0')
)(POPUP_TEMPLATE_IDS);

export const navigate = (draftState, { isForward, index }) => {
    const activeLayerId = draftState.layerIds[draftState.layerIds.length - 1];
    const activeLayer = draftState.layers[activeLayerId];
    const activeLayerMeta = draftState.layersMeta[activeLayerId];
    const { activePopupIndex: prevActivePopupIndex } = activeLayerMeta;
    let activePopupIndex;

    if (isForward != null) {
        activePopupIndex = isForward ? prevActivePopupIndex + 1 : prevActivePopupIndex - 1;
    } else if (index != null) {
        activePopupIndex = index;
    } else {
        return;
    }

    if (activePopupIndex === prevActivePopupIndex || activePopupIndex < 0 || activePopupIndex >= activeLayer.length) {
        return draftState;
    }

    draftState.layersMeta[activeLayerId].activePopupIndex = activePopupIndex;

    return draftState;
};

/**
 * Popup options
 * @param {string} options.id
 * @param {Object} options.component
 * @param {Function} options.render
 * @param {string} options.layer
 * @param {boolean} options.isModal
 * @param {boolean} options.addFirst
 * @param {string} options.singletonMode
 * @param {string} options.title
 * @param {string} options.content
 * @param {Function} options.onClose
 * @param {Function} options.closeAll
 * @returns {Object}
 */

export const createPopup = ({ id = uuid(), layer, addFirst = false, singletonMode = SINGLETON_MODE.PERSIST, ...options }) => ({
    id,
    addFirst,
    singletonMode,
    layerName: layer,
    layerId: getLayerId(layer),
    ...options
});

export const closePopup = (state, popupId) => {
    const { popups } = state;

    if (isEmpty(popups)) {
        return state;
    }

    let { layerIds, layers, layersMeta } = state;
    let id = popupId;

    if (id == null) {
        const activeLayerId = last(layerIds);
        const activeLayer = layers[activeLayerId];
        const { activePopupIndex } = layersMeta[activeLayerId];
        id = activeLayer[activePopupIndex];
    } else if (!popups[id]) {
        return state;
    }

    const { layerId = 0 } = popups[id];
    const { [layerId]: layer } = layers;
    layers = { ...layers };

    if (layer.length === 1) {
        layerIds = layerIds.filter(lId => lId !== layerId);
        delete layers[layerId];

        layersMeta = { ...layersMeta };
        delete layersMeta[layerId];
    } else {
        layers[layerId] = layer.filter(pId => pId !== id);

        const layerMeta = layersMeta[layerId];
        const { activePopupIndex } = layerMeta;
        if (activePopupIndex === layer.length - 1) {
            ({ layersMeta } = navigate(state, { isForward: false }));
        }
    }

    return {
        ...state,
        popups: omit([id], popups),
        layerIds,
        layers,
        layersMeta
    };
};

export const closePopups = (state, payload = {}, defaultState) => {
    const { popups, layers } = state;

    if (isEmpty(popups)) {
        return state;
    }

    const { popups: include, exclude, excludeLayers } = payload;

    if (!include && !exclude && !excludeLayers) {
        return defaultState;
    }

    const getLayersPopups = layersNames => pipe(
        pick(layersNames.map(getLayerId)),
        values,
        flatten
    )(layers);

    const popupsToClose = pipe(
        keys,
        include ? intersection(include) : identity,
        exclude ? flip(difference)(exclude) : identity,
        excludeLayers ? flip(difference)(getLayersPopups(excludeLayers)) : identity
    )(popups);

    if (popupsToClose.length === 0) {
        return state;
    }

    return reduce(closePopup, state)(popupsToClose);
};

export const addPopup = (state, popup) => {
    const { popups, layerIds, layers, layersMeta } = state;
    const { id, layerId, layerName, addFirst } = popup;

    const nextPopups = assoc([id], popup, popups);

    // layer Ids must be numeric and sorted ascending
    const nextLayerIds = layerIds.includes(layerId)
        ? layerIds
        : [...layerIds, layerId].sort((a, b) => a - b);

    // popups in layer are ordered chronologically
    const nextLayers = over(
        lensProp(layerId),
        (addFirst ? prepend : append)(id),
        layers
    );

    const nextLayersMeta = layersMeta[layerId] != null
        ? layersMeta
        : {
            ...layersMeta,
            [layerId]: {
                ...layersMetaDefault[layerName],
                activePopupIndex: 0
            }
        };

    return {
        ...state,
        popups: nextPopups,
        layerIds: nextLayerIds,
        layers: nextLayers,
        layersMeta: nextLayersMeta
    };
};

export const addPopupOperation = (state, action, defaultState) => {
    const { popups } = state;
    const popup = createPopup(action.payload);
    const { id, closeAll, singletonMode } = popup;
    let nextState;

    // singleton mode - implemented by static popup id which should be defined in action
    if (popups[id] != null) {
        if (closeAll) {
            if (singletonMode === SINGLETON_MODE.UPDATE) {
                // reset state, add new
                nextState = addPopup(defaultState, popup);
            } else {
                // close all but one
                nextState = closePopups(state, { exclude: [id] });
            }
        } else {
            if (singletonMode === SINGLETON_MODE.UPDATE) {
                // replace one
                nextState = assocPath(['popups', id], popup, state);
            } else {
                // do nothing
                nextState = state;
            }
        }
    } else {
        if (closeAll) {
            const alwaysVisiblePopups = Object.values(state.popups)
                .filter(item => state.layersMeta[item.layerId]?.alwaysVisible);

            if (alwaysVisiblePopups.length) {
                nextState = alwaysVisiblePopups.reduce((acc, pageInPopup) => addPopup(acc, pageInPopup), defaultState);
                nextState = addPopup(nextState, popup);
            } else {
                nextState = addPopup(defaultState, popup);
            }
        } else {
            // add new
            nextState = addPopup(state, popup);
        }
    }

    return nextState;
};
